import DiscountCard from "@components/DiscountCard";
import InnerContainerImageGradient from "@components/InnerContainerImageGradient";
import SolidHeader from "@components/SolidHeader";
import styled from "@emotion/styled";
import Resizer from "@hocs/Resizer";
import loadable from "@loadable/component";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { initAppBrowser } from "../actions/initApp";
import { defaultAction } from "../actions/defaultAction";
import { SORT_DROP_DOWN } from "../constants/actionTypes";

const BuyPageCardsHeader = loadable(() =>
  import(
    /* webpackChunkName: "BuyPageCardsHeader" */ "@components/BuyPageCardsHeader"
  )
);
const Dropdown = loadable(() =>
  import(/* webpackChunkName: "Dropdown" */ "@components/Dropdown")
);
const CategoriesContainerLargeScreens = loadable(() =>
  import(
    /* webpackChunkName: "CategoriesContainerLargeScreens" */ "@components/CategoriesContainerLargeScreens"
  )
);
const QuickBuyMobileButton = loadable(
  () =>
    import(
      /* webpackChunkName: "QuickBuyMobileButton" */ "@components/QuickBuyMobileButton"
    ),
  { ssr: false }
);
const BuyTabletButtons = loadable(() =>
  import(
    /* webpackChunkName: "BuyTabletButtons" */ "@components/BuyTabletButtons"
  )
);
const CardCashSpinner = loadable(() =>
  import(
    /* webpackChunkName: "CardCashSpinner" */ "../components/CardCashSpinner"
  )
);

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  width: 100%;
  grid-gap: 20px;
  grid-row-gap: 82px;
  margin-top: 67px;
  @media (max-width: 1300px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
  @media (max-width: 925px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 767px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
  @media (max-width: 575px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 450px) {
    margin-top: 73px;
  }
`;

const LoadMoreButton = styled.div`
  border-radius: 10px;
  height: 60px;
  padding-right: 50px;
  padding-left: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #12b2ea;
  background-color: rgba(18, 178, 234, 0.1);
  width: fit-content;
  font-weight: bold;
  width: fit-content;
  cursor: pointer;
  margin: 0 auto;
  margin-top: 50px;
  font-size: 20px;
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 250px 1fr;
  margin-top: 40px;
  grid-gap: 30px;
  margin-bottom: 110px;
  @media (max-width: 950px) {
    grid-gap: 0px;
  }
  @media (max-width: 950px) {
    grid-gap: 0px;
  }
  @media (max-width: 767px) {
    display: block;
  }
`;

const CardsHeaderDiv = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  @media (max-width: 500px) {
    grid-template-columns: 1.4fr 1fr;
    grid-gap: 15px;
  }
  @media (max-width: 440px) {
    grid-template-columns: 1.6fr 1fr;
  }
  @media (max-width: 375px) {
    grid-template-columns: 1.3fr 1fr;
  }
  @media (max-width: 340px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const DropdownContainerDiv = styled.div`
  width: 227px;
  justify-self: flex-end;
  @media (max-width: 500px) {
    width: auto;
    justify-self: flex-start;
  }
`;

const Main = styled.div`
  position: relative;
`;

const Header = styled.h1`
  margin: 0;
  font-size: 60px;
  padding-top: 123px;
  padding-bottom: 45px;
  font-weight: 500;
  line-height: 1.1;
  margin-bottom: 0.5rem;
  color: #fff;
  @media (max-width: 1000px) {
    font-size: 50px;
  }
  @media (max-width: 767px) {
    font-size: 36px;
    padding-top: 86px;
    padding-bottom: 38px;
  }
  @media (max-width: 600px) {
    width: 244px;
    margin: 0 auto;
    line-height: 1.33;
    padding-top: 64px;
  }
`;

const SuperDiscountBrands = ({}) => {
  const dispatch = useDispatch();

  const sortState = useSelector(state => state.userActions.filterDropDown);
  const merchantsBuy = useSelector(state => state.merchantsBuy);
  const epicDiscountsImage = useSelector(
    state => state.logos.epicDiscountsBanner
  );
  const currentSearch = useSelector(state => state.userActions.buySearchText);

  const [loadingStage, setLoadingStage] = useState(0);
  const [slicer, setSlicer] = useState(28);

  useEffect(() => {
    dispatch(initAppBrowser(["messages", "categories", "buy"]));
    dispatch(defaultAction(SORT_DROP_DOWN, "Highest Discount"));
  }, []);

  const merchants = merchantsBuy?.sortedByName || merchantsBuy;
  const merchantsArray = Object.values(merchants);

  if (!merchants?.length) {
    return (
      <div style={{ paddingTop: 200 }}>
        <CardCashSpinner />
      </div>
    );
  }

  const merchantSort = (a, b) => {
    if (sortState === "Highest Discount") {
      return b.upToPercentage - a.upToPercentage;
    } else if (sortState === "Most Popular") {
      return b.cardsAvailable - a.cardsAvailable;
    } else if (sortState === "Alphabetical") {
      return a.name.localeCompare(b.name);
    }
  };

  const superDiscountedMerchants = merchantsArray
    .filter(merchant => merchant.upToPercentage >= 20)
    .sort(merchantSort);

  return (
    <div
      className={`mar-t-128-108 discount-buy-main-page-container orange-caret`}
    >
      <Helmet
        title={`Epic 20%+ discounted gift cards | CardCash`}
        meta={[
          {
            name: "description",
            content:
              "Explore the collection of gift card brands with discounts of 20% or more. For savvy shoppers seeking epic savings. Learn how to be a better saver. Savings tips for the savviest savers from CardCash, the world's largest gift card exchange."
          }
        ]}
      />
      <Resizer
        components={[
          {
            component: () => <QuickBuyMobileButton setDisplay={"768px"} />,
            break: 1,
            maxBreak: 768
          }
        ]}
      />
      <div>
        <InnerContainerImageGradient />
        <Main>
          <SolidHeader
            categorySaleImage={epicDiscountsImage || false}
            setClassName={
              epicDiscountsImage
                ? "buy-header-category buy-header-category-no-opacity"
                : "buy-header-main-all-cards"
            }
            styles={{
              color: "#fff"
            }}
          >
            {!epicDiscountsImage && (
              <Header className="buy-header-h4">Epic 20%+ discounts</Header>
            )}
          </SolidHeader>
        </Main>
        <MainContent className="main-container-page-width">
          <Resizer
            components={[
              {
                component: () => (
                  <CategoriesContainerLargeScreens
                    guaranteeText={"45-day guarantee"}
                  />
                ),
                break: 767,
                maxBreak: Number.MAX_SAFE_INTEGER
              }
            ]}
            defaultComponent={() => (
              <CategoriesContainerLargeScreens
                guaranteeText={"45-day guarantee"}
              />
            )}
          />

          <div>
            <Resizer
              components={[
                {
                  component: () => (
                    <BuyTabletButtons guaranteeText={"45-day guarantee"} />
                  ),
                  break: 1,
                  maxBreak: 768
                }
              ]}
              defaultComponent={() => (
                <BuyTabletButtons guaranteeText={"45-day guarantee"} />
              )}
            />
            <CardsHeaderDiv>
              <BuyPageCardsHeader page={"buyCategories"} />
              <DropdownContainerDiv>
                <Dropdown />
              </DropdownContainerDiv>
            </CardsHeaderDiv>
            <GridContainer className="animated fadeIn">
              {superDiscountedMerchants
                .slice(0, currentSearch?.length > 0 ? 3000 : slicer)
                .map(card => (
                  <DiscountCard
                    key={card.id}
                    moveToMerchantDetail={card.slug}
                    name={card.name}
                    cardsAvailable={card.cardsAvailable}
                    upToPercentage={card.upToPercentage}
                    img={card.image}
                  />
                ))}
            </GridContainer>
            {superDiscountedMerchants.length > slicer &&
              loadingStage < 2 &&
              currentSearch.length < 1 && (
                <LoadMoreButton
                  onClick={() => {
                    setSlicer(loadingStage === 0 ? 128 : 3000);
                    setLoadingStage(loadingStage + 1);
                  }}
                >
                  {loadingStage === 0 ? "Load more" : "Load all"}
                </LoadMoreButton>
              )}
          </div>
        </MainContent>
      </div>
    </div>
  );
};

export default SuperDiscountBrands;
